import { useState, useEffect } from "react";
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import LoadingSpinner from '../components/LoadingSpinner';
import { MdDeleteForever } from "react-icons/md";

export default function Notifications() {
  const [message, setMessage] = useState("");
  const [subject, setSubject] = useState("");
  const [type, setType] = useState("email");
  const [sentNotifications, setSentNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const navigate = useNavigate();

  const getToken = () => localStorage.getItem('adminToken');

  const fetchNotifications = async () => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.get(
        'https://awriter-backend-si7g.onrender.com/api/admin/notifications?page=1&limit=50',
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Notifications API Response:', response.data);
      setSentNotifications(response.data.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error.message, error.response?.data);
      setErrorMessage(error.response?.data?.error || 'Failed to fetch notifications');
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSend = async () => {
    if (!message.trim()) {
      setErrorMessage('Message is required');
      return;
    }
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      const response = await axios.post(
        'https://awriter-backend-si7g.onrender.com/api/admin/notify',
        { message, subject: subject.trim() || undefined, type },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Send Notification Response:', response.data);
      
      // Show success message with stats if available
      if (response.data.stats) {
        const { messagesSent, successCount, errorCount, usersWithoutTokens } = response.data.stats;
        setErrorMessage(
          `✅ Notification sent! Delivered: ${messagesSent}, Success: ${successCount}, Errors: ${errorCount}, Users without tokens: ${usersWithoutTokens}`
        );
      } else {
        setErrorMessage('✅ Notification sent successfully');
      }
      
      setMessage('');
      setSubject('');
      setType('email');
      await fetchNotifications(); // Refresh sent notifications
    } catch (error) {
      console.error('Error sending notification:', error.message, error.response?.data);
      
      // Show detailed error info including stats
      let errorMsg = 'Failed to send notification';
      if (error.response?.data?.error) {
        errorMsg = error.response.data.error;
        if (error.response.data.stats) {
          const stats = error.response.data.stats;
          errorMsg += ` — Total: ${stats.totalUsers}, Success: ${stats.successCount || 0}, Failed: ${stats.errorCount || 0}, No tokens: ${stats.usersWithoutTokens || 0}`;
        }
        if (error.response.data.hint) {
          errorMsg += ` — ${error.response.data.hint}`;
        }
      }
      setErrorMessage(errorMsg);
      
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      setIsLoading(true);
      setErrorMessage(null);
      const token = getToken();
      if (!token) {
        throw new Error('No authentication token found');
      }
      console.log('Attempting to delete notification:', { notificationId });
      const response = await axios.delete(
        `https://awriter-backend-si7g.onrender.com/api/admin/notifications/${notificationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      console.log('Delete Notification Response:', response.data);
      setSentNotifications(sentNotifications.filter((notif) => notif.id !== notificationId));
      setErrorMessage('Notification deleted successfully');
    } catch (error) {
      console.error('Error deleting notification:', error.message, error.response?.data);
      const errorMsg = error.response?.status === 404
        ? 'Notification not found'
        : error.response?.status === 401
        ? 'Unauthorized: Please log in again'
        : error.response?.data?.error || 'Failed to delete notification';
      setErrorMessage(errorMsg);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('adminData');
        localStorage.removeItem('rememberedEmail');
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-full max-w-2xl p-6">
        <h2 className="text-2xl font-bold mb-6">Send Notification to All Users</h2>

        <div className="bg-white p-6 rounded-lg shadow space-y-4">
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Notification Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="border rounded p-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="email">Email</option>
              <option value="push">Push</option>
            </select>
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Subject</label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter your subject"
              className="w-full p-3 border rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-sm font-medium mb-1">Message</label>
            <textarea
              rows="4"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write your update to all users here..."
              className="w-full p-3 border rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <button
            onClick={handleSend}
            disabled={isLoading}
            className="bg-[#000000] hover:bg-black/50 duration-700 text-white px-4 py-2 rounded-md shadow disabled:bg-gray-400"
          >
            {isLoading ? 'Sending...' : 'Send Notification'}
          </button>
          {errorMessage && (
            <div className={`p-2 text-center rounded ${errorMessage.includes('successfully') ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {errorMessage}
            </div>
          )}
        </div>

        {sentNotifications.length > 0 && (
          <div className="mt-10">
            <h3 className="text-xl font-semibold mb-4">Sent Notifications</h3>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <ul className="space-y-3">
                {sentNotifications.map((notif) => (
                  <li key={notif.id} className="bg-gray-100 p-3 rounded-md border flex lg:justify-between lg:items-center items-start">
                    <div>
                      <p className="font-medium">{notif.subject}</p>
                      <p>{notif.message}</p>
                      <p className="text-sm text-gray-500">
                        To: {notif.userEmail || 'All Users'} | Type: {notif.type} |{' '}
                        {format(new Date(notif.createdAt), 'yyyy-MM-dd HH:mm:ss')}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(notif.id)}
                      className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-md"
                    >
                      <MdDeleteForever className="text-2xl" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}